import {
  Component,
  Input,
  OnInit,
  Optional,
  Self,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { GlPeriod, parseValueToGlPeriod } from './gl-period';

@Component({
  selector: 'app-gl-period-input',
  templateUrl: './gl-period-input.component.html',
  styleUrls: ['./gl-period-input.component.scss'],
  providers: [],
})
export class GlPeriodInputComponent implements OnInit, ControlValueAccessor {
  @Input()
  addedPeriods: string[] = [];

  glPeriod: GlPeriod;
  displayValue: string;

  isYearModeActive = false;
  buttonIndex = 0;

  months: string[] = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sept',
    'Oct',
    'Nov',
    'Dec',
  ];
  mask = 'yyyymm';

  @ViewChild(NgbDropdown)
  dropDownCalender: NgbDropdown;

  @Input()
  labelName: string;

  @Input()
  hideLabel = false;

  @Input()
  flagAsRequired = false;

  @Input()
  iconButton = 'calender';

  @Input() disabled = false;

  private _minimumPeriod: GlPeriod;
  get minimumPeriod(): GlPeriod {
    return this._minimumPeriod;
  }

  private _maximumPeriod: GlPeriod;
  get maximumPeriod(): GlPeriod {
    return this._maximumPeriod;
  }

  @Input()
  set minimumPeriod(value: string) {
    if (value) {
      this._minimumPeriod = parseValueToGlPeriod(value);
    } else {
      this._minimumPeriod = null;
    }
  }
  @Input()
  set maximumPeriod(value: string) {
    if (value) {
      this._maximumPeriod = parseValueToGlPeriod(value);
    } else {
      this._maximumPeriod = null;
    }
  }

  constructor(
    @Self()
    @Optional()
    public ngControl: NgControl,
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.months = this.months.concat(...this.addedPeriods);
  }

  handleUserInput(value: string) {
    const data = this.parsePeriod(value);
    this.onChange(data);
  }

  parsePeriod(value: string) {
    const items = value;

    if (items.length === 6) {
      const rawYear = items.substring(0, 4);
      const rawMonth = items.substring(4, 6);

      let month = this.months.indexOf(rawMonth);
      if (month < 0) {
        month = parseInt(rawMonth, 10);
      } else {
        month = month + 1;
      }

      let year = parseInt(rawYear, 10);

      if (year < 100) {
        year = year + 2000;
      }

      return {
        year,
        month,
      };
    }
    return null;
  }

  selectYearMonth(event: any, index: number) {
    if (this.isYearModeActive) {
      event.stopPropagation();
      this.glPeriod.year = index + this.buttonIndex;
      this.isYearModeActive = false;
      this.buttonIndex = this.getIndexYearValue(this.glPeriod.year);
    } else {
      this.glPeriod.month = index + 1;
      this.dropDownCalender.close();
    }
    this.onChange(this.glPeriod);

    this.updateDisplayValue();
  }

  showYear(event: any, show: boolean) {
    event.stopPropagation();
    this.isYearModeActive = !this.isYearModeActive;
  }

  addYear(event: any, buttonIndex: number) {
    event.stopPropagation();
    const year = this.isYearModeActive
      ? this.glPeriod.year + 10 * buttonIndex
      : this.glPeriod.year + buttonIndex;

    this.glPeriod.year = year;
    this.buttonIndex = this.getIndexYearValue(year);
    this.updateDisplayValue();
  }

  updateDisplayValue() {
    this.displayValue = this.formatData(this.glPeriod);
  }

  getIndexYearValue(year: number): number {
    return year - (year % 10) - 1;
  }

  formatData(data: GlPeriod): string {
    const parsedMonth = data.month.toLocaleString('en-US', {
      minimumIntegerDigits: 2,
      useGrouping: false,
    });
    return data.year + '' + parsedMonth;
  }

  writeValue(data: GlPeriod): void {
    this.glPeriod = data;
    this.buttonIndex = this.getIndexYearValue(this.glPeriod.year);
    this.updateDisplayValue();
  }

  private onChange = (data: GlPeriod) => {};
  private onTouched = () => {};

  registerOnChange(fn: (data: GlPeriod) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  getIsSelectionValid(index: number) {
    let isValid = true;

    isValid = this.minimumPeriod
      ? this.isYearModeActive
        ? this.buttonIndex + index >= this._minimumPeriod.year
        : this.glPeriod.year > this._minimumPeriod.year ||
          (this.glPeriod.year === this._minimumPeriod.year &&
            index + 1 >= this._minimumPeriod.month)
      : true;

    if (isValid) {
      isValid = this.maximumPeriod
        ? this.isYearModeActive
          ? this.buttonIndex + index <= this._maximumPeriod.year
          : this.glPeriod.year < this._maximumPeriod.year ||
            (this.glPeriod.year === this._maximumPeriod.year &&
              index + 1 <= this._maximumPeriod.month)
        : true;
    }

    return isValid;
  }

  isMinValidYear(index: number) {
    if (this._minimumPeriod) {
      const year = this.isYearModeActive
        ? this.glPeriod.year + 10 * index
        : this.glPeriod.year + index;
      return year >= this.minimumPeriod.year;
    }

    return true;
  }
  isMaxValidYear(index: number) {
    if (this._maximumPeriod) {
      const year = this.isYearModeActive
        ? this.glPeriod.year + 10 * index
        : this.glPeriod.year + index;
      return year <= this.maximumPeriod.year;
    }
    return true;
  }
}
